import { useState, type ComponentType, type ReactNode } from "react";
import { AlertCircle, Check, FileText, Link as LinkIcon, Play } from "lucide-react";
import { cn } from "../lib/utils";

/**
 * One source the user is making posts from. Onboarding carries it through
 * setup and Generate lists it, in the same card, so it reads as the same thing.
 */
export function SourceCard({
  icon,
  favicon,
  thumbnailUrl,
  title,
  details = [],
  quote,
  status = "ok",
  note,
  action,
  onEdit,
  children,
  label = "Source for your post",
}: {
  /** Shown when there is no favicon or thumbnail. */
  icon?: ComponentType<{ className?: string }>;
  favicon?: string;
  /** A video's thumbnail; replaces the icon tile with a wider frame. */
  thumbnailUrl?: string;
  title?: string;
  /** Muted lines under the title (the link, channel · length). */
  details?: string[];
  /** Pasted text, shown quoted in place of a title. */
  quote?: string;
  /** "unchecked": accepted but not read yet. "error": can't be used. */
  status?: "ok" | "unchecked" | "error";
  note?: ReactNode;
  action?: ReactNode;
  /** Makes the quote clickable to edit it (pasted text). */
  onEdit?: () => void;
  /** Follow-up controls that belong to this source, under a divider. */
  children?: ReactNode;
  label?: string;
}) {
  const [faviconFailed, setFaviconFailed] = useState(false);
  const video = !!thumbnailUrl && status !== "error";
  const Fallback = icon ?? (quote ? FileText : LinkIcon);
  return (
    <div
      role="group"
      aria-label={label}
      className={cn(
        "rounded-2xl border bg-card shadow-sm",
        status === "error" && "border-destructive/30 bg-destructive/5",
      )}
    >
      <div className="flex items-center gap-3.5 p-3">
        <span
          aria-hidden="true"
          className={cn(
            "relative grid h-11 shrink-0 place-items-center rounded-xl",
            video ? "w-[4.75rem] bg-muted" : "w-11",
            !video && (status === "error"
              ? "bg-destructive/10 text-destructive"
              : status === "unchecked"
                ? "bg-amber-100 text-amber-800"
                : "bg-[oklch(var(--brand)/0.1)] text-brand-ink"),
          )}
        >
          {video ? (
            <>
              {/* Pinned to the tile: as a grid item, h-full follows the row, which grows to the image's 4:3 height. */}
              <img src={thumbnailUrl} alt="" referrerPolicy="no-referrer" className="absolute inset-0 h-full w-full rounded-xl object-cover" />
              <span className="absolute inset-0 grid place-items-center rounded-xl bg-black/15">
                <Play className="h-3.5 w-3.5 fill-white text-white" />
              </span>
            </>
          ) : favicon && !faviconFailed && status !== "error" ? (
            <img src={favicon} alt="" width={20} height={20} referrerPolicy="no-referrer" className="h-5 w-5 rounded-sm" onError={() => setFaviconFailed(true)} />
          ) : (
            <Fallback className="h-5 w-5" />
          )}
          <span
            className={cn(
              "absolute -bottom-1 -right-1 grid h-4 w-4 place-items-center rounded-full ring-2 ring-card",
              status === "error" ? "bg-destructive" : status === "unchecked" ? "bg-amber-500" : "bg-brand",
            )}
          >
            {status === "ok"
              ? <Check className="h-2.5 w-2.5 text-white" strokeWidth={3} />
              : <AlertCircle className="h-2.5 w-2.5 text-white" strokeWidth={3} />}
          </span>
        </span>
        <div className={cn("min-w-0 flex-1 text-sm", !action && "pr-2")}>
          {quote && onEdit ? (
            <button
              type="button"
              onClick={onEdit}
              aria-label="Edit pasted text"
              className="line-clamp-2 w-full rounded text-left leading-snug hover:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
            >
              &ldquo;{quote.slice(0, 160)}{quote.length > 160 ? "…" : ""}&rdquo;
            </button>
          ) : quote ? (
            <p className="line-clamp-2 leading-snug">
              &ldquo;{quote.slice(0, 160)}{quote.length > 160 ? "…" : ""}&rdquo;
            </p>
          ) : (
            <p className="truncate font-semibold" title={title}>{title}</p>
          )}
          {details.filter(Boolean).map((line) => (
            <p key={line} className="truncate text-muted-foreground" title={line}>{line}</p>
          ))}
          {note}
        </div>
        {action}
      </div>
      {children && <div className="border-t border-border/60 px-3 py-3">{children}</div>}
    </div>
  );
}

/** A link as people read it: no scheme, no www. */
export function displayLink(link: string) {
  return link.replace(/^https?:\/\/(www\.)?/, "");
}
